import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Tv, QrCode, Clock, Users, Gavel, ShoppingBag, Home as HomeIcon,
  ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX,
  Maximize2, Minimize2, RefreshCw, Settings, X, Sparkles, Zap,
  LayoutGrid, Layers, Monitor, Palette, Star, Download
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';

// Display Templates Configuration
const TEMPLATES = {
  hyper: {
    id: 'hyper',
    name: 'Hyper Neon',
    description: 'Cyberpunk-inspired with glowing effects',
    preview: 'bg-gradient-to-br from-violet-600 via-fuchsia-600 to-cyan-500'
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal Dark',
    description: 'Clean and professional',
    preview: 'bg-gradient-to-br from-gray-900 to-black'
  },
  luxury: {
    id: 'luxury',
    name: 'Luxury Gold',
    description: 'Premium gold accents',
    preview: 'bg-gradient-to-br from-amber-900 via-yellow-800 to-amber-950'
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Wave',
    description: 'Calming blue gradients',
    preview: 'bg-gradient-to-br from-blue-900 via-cyan-800 to-teal-900'
  },
  matrix: {
    id: 'matrix',
    name: 'Matrix Code',
    description: 'Green digital aesthetic',
    preview: 'bg-gradient-to-br from-emerald-950 via-green-900 to-black'
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Vibes',
    description: 'Warm gradient colors',
    preview: 'bg-gradient-to-br from-orange-600 via-rose-600 to-purple-700'
  }
};

// Hyper Neon Template Component
const HyperTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col relative overflow-hidden bg-black">
    {/* Animated background effects */}
    <div className="absolute inset-0">
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/30 rounded-full blur-[150px] animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/30 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-fuchsia-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />
    </div>

    {/* Top Bar */}
    <div className="relative z-10 flex items-center justify-between p-6 border-b border-white/10 backdrop-blur-xl bg-black/30">
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-2xl blur-lg opacity-60" />
          <div className="relative p-4 bg-black/80 rounded-2xl border border-white/20">
            {getTypeIcon()}
          </div>
        </div>
        <div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
            {getTypeTitle()}
          </h1>
          <p className="text-white/50 font-medium">ANANT Web Portal</p>
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        <div className="text-right">
          <div className="text-5xl font-black font-mono bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
            {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-white/40">
            {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
          </div>
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Item Display */}
        <div className="lg:col-span-2">
          <div className="relative group">
            {/* Glow effect */}
            <div className="absolute -inset-2 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-500 rounded-[2rem] blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
            
            <div className="relative aspect-video rounded-3xl overflow-hidden bg-black border border-white/20">
              {currentItem?.image ? (
                <img 
                  src={getImageUrl(currentItem.image)} 
                  alt={currentItem.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-900/50 to-cyan-900/50">
                  {getTypeIcon()}
                </div>
              )}
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              
              {/* Badge */}
              <div className="absolute top-4 left-4">
                <span className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full text-sm font-black uppercase tracking-widest shadow-lg shadow-violet-500/50">
                  {currentItem?.badge}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/10">
                <div 
                  className="h-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Item Details */}
          <div className="mt-8 text-center">
            <h2 className="text-5xl md:text-6xl font-black mb-4 line-clamp-2 bg-gradient-to-r from-white via-violet-200 to-cyan-200 bg-clip-text text-transparent">
              {currentItem?.title}
            </h2>
            <p className="text-4xl md:text-5xl font-black bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent mb-4">
              {currentItem?.subtitle}
            </p>
            <p className="text-xl text-white/50 font-medium">
              {currentItem?.detail1}
            </p>
          </div>
        </div>

        {/* QR Code Panel */}
        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="relative group">
              <div className="absolute -inset-3 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-[2rem] blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
              <div className="relative bg-white p-8 rounded-3xl">
                <QRCodeSVG 
                  value={qrUrl}
                  size={280}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>
            <p className="text-center mt-6 text-2xl text-white/70 font-bold">
              Scan to view
            </p>
            <p className="text-center text-white/40 mt-2 font-medium">
              {items.length} items • {currentIndex + 1} of {items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Minimal Dark Template Component
const MinimalTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col bg-[#0a0a0a]">
    {/* Top Bar */}
    <div className="flex items-center justify-between p-8 border-b border-white/5">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-white/5 rounded-xl">
          {getTypeIcon()}
        </div>
        <div>
          <h1 className="text-3xl font-semibold text-white">{getTypeTitle()}</h1>
          <p className="text-white/30">ANANT Web Portal</p>
        </div>
      </div>
      
      <div className="text-right">
        <div className="text-4xl font-light text-white font-mono">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-white/30 text-sm">
          {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-12">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/5">
            {currentItem?.image ? (
              <img 
                src={getImageUrl(currentItem.image)} 
                alt={currentItem.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {getTypeIcon()}
              </div>
            )}
          </div>
          
          {/* Progress */}
          <div className="mt-4 h-0.5 bg-white/5 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white/50 transition-all duration-1000 ease-linear"
              style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
            />
          </div>

          <div className="mt-8">
            <div className="inline-block px-3 py-1 bg-white/5 rounded-full text-xs text-white/50 uppercase tracking-widest mb-4">
              {currentItem?.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4">
              {currentItem?.title}
            </h2>
            <p className="text-3xl text-white/70 font-light mb-2">
              {currentItem?.subtitle}
            </p>
            <p className="text-white/30">
              {currentItem?.detail1}
            </p>
          </div>
        </div>

        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="bg-white p-6 rounded-2xl">
              <QRCodeSVG 
                value={qrUrl}
                size={220}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="text-center mt-6 text-white/50">
              Scan to view details
            </p>
            <p className="text-white/30 text-sm mt-1">
              {currentIndex + 1} / {items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Luxury Gold Template Component
const LuxuryTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col bg-gradient-to-br from-[#1a1307] via-[#0f0a04] to-black relative overflow-hidden">
    {/* Golden particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-amber-500/40 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }}
        />
      ))}
    </div>

    {/* Ornamental corners */}
    <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-amber-500/30" />
    <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-amber-500/30" />
    <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-amber-500/30" />
    <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-amber-500/30" />

    {/* Top Bar */}
    <div className="relative z-10 flex items-center justify-between p-8">
      <div className="flex items-center gap-4">
        <div className="p-4 border-2 border-amber-500/50 rounded-xl bg-amber-500/10">
          <div className="text-amber-500">{getTypeIcon()}</div>
        </div>
        <div>
          <h1 className="text-4xl font-serif text-amber-100">{getTypeTitle()}</h1>
          <p className="text-amber-500/50 font-light tracking-[0.3em] text-sm uppercase">Premium Collection</p>
        </div>
      </div>
      
      <div className="text-right">
        <div className="text-5xl font-serif text-amber-100 font-light">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-amber-500/50 tracking-widest text-sm">
          {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/30 via-amber-300/20 to-amber-500/30 rounded-2xl blur" />
            <div className="relative aspect-video rounded-xl overflow-hidden border-2 border-amber-500/30 bg-black">
              {currentItem?.image ? (
                <img 
                  src={getImageUrl(currentItem.image)} 
                  alt={currentItem.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-900/30 to-black">
                  <div className="text-amber-500">{getTypeIcon()}</div>
                </div>
              )}
              
              {/* Badge */}
              <div className="absolute top-4 left-4">
                <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-400 rounded-full text-xs font-bold text-black uppercase tracking-widest">
                  {currentItem?.badge}
                </span>
              </div>

              {/* Progress */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                <div 
                  className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-1000"
                  style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <h2 className="text-5xl font-serif text-amber-100 mb-4 line-clamp-2">
              {currentItem?.title}
            </h2>
            <p className="text-4xl text-amber-400 font-light mb-3">
              {currentItem?.subtitle}
            </p>
            <p className="text-amber-500/50 tracking-wide">
              {currentItem?.detail1}
            </p>
          </div>
        </div>

        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/30 to-amber-300/30 rounded-2xl blur" />
              <div className="relative bg-amber-50 p-6 rounded-xl border-2 border-amber-500/50">
                <QRCodeSVG 
                  value={qrUrl}
                  size={240}
                  level="H"
                  includeMargin={true}
                  fgColor="#92400e"
                />
              </div>
            </div>
            <p className="text-center mt-6 text-xl text-amber-300 font-serif">
              Scan for Details
            </p>
            <p className="text-amber-500/40 mt-2">
              {currentIndex + 1} of {items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Ocean Wave Template
const OceanTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 relative overflow-hidden">
    {/* Wave animation background */}
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-cyan-500/10 to-transparent" />
      <svg className="absolute bottom-0 w-full h-40 text-cyan-500/10" preserveAspectRatio="none" viewBox="0 0 1440 320">
        <path fill="currentColor" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
          <animate attributeName="d" dur="10s" repeatCount="indefinite" values="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;M0,128L48,154.7C96,181,192,235,288,234.7C384,235,480,181,576,181.3C672,181,768,235,864,250.7C960,267,1056,245,1152,229.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
        </path>
      </svg>
    </div>

    {/* Top Bar */}
    <div className="relative z-10 flex items-center justify-between p-8">
      <div className="flex items-center gap-4">
        <div className="p-4 bg-cyan-500/20 rounded-2xl backdrop-blur-sm border border-cyan-400/20">
          <div className="text-cyan-400">{getTypeIcon()}</div>
        </div>
        <div>
          <h1 className="text-4xl font-bold text-white">{getTypeTitle()}</h1>
          <p className="text-cyan-300/50">ANANT Web Portal</p>
        </div>
      </div>
      
      <div className="text-right backdrop-blur-sm bg-white/5 rounded-2xl p-4 border border-white/10">
        <div className="text-4xl font-bold text-cyan-100 font-mono">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-cyan-300/50 text-sm">
          {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="aspect-video rounded-3xl overflow-hidden bg-black/30 backdrop-blur-sm border border-cyan-400/20 shadow-2xl shadow-cyan-500/20">
            {currentItem?.image ? (
              <img 
                src={getImageUrl(currentItem.image)} 
                alt={currentItem.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-cyan-400">{getTypeIcon()}</div>
              </div>
            )}
            
            <div className="absolute top-4 left-4">
              <span className="px-4 py-2 bg-cyan-500/80 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                {currentItem?.badge}
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <div 
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-400 transition-all duration-1000"
                style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
              />
            </div>
          </div>

          <div className="mt-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-4 line-clamp-2">
              {currentItem?.title}
            </h2>
            <p className="text-4xl text-cyan-300 font-bold mb-3">
              {currentItem?.subtitle}
            </p>
            <p className="text-cyan-400/60">
              {currentItem?.detail1}
            </p>
          </div>
        </div>

        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="bg-white p-6 rounded-3xl shadow-2xl shadow-cyan-500/30">
              <QRCodeSVG 
                value={qrUrl}
                size={240}
                level="H"
                includeMargin={true}
                fgColor="#0891b2"
              />
            </div>
            <p className="text-center mt-6 text-xl text-cyan-200">
              Scan to explore
            </p>
            <p className="text-cyan-400/50 mt-2">
              {currentIndex + 1} of {items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Matrix Template
const MatrixTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col bg-black relative overflow-hidden">
    {/* Matrix rain effect (simplified CSS version) */}
    <div className="absolute inset-0 opacity-20">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute text-emerald-500 text-xs font-mono whitespace-pre"
          style={{
            left: `${i * 5}%`,
            animation: `matrixFall ${3 + Math.random() * 5}s linear infinite`,
            animationDelay: `${Math.random() * 3}s`
          }}
        >
          {Array.from({ length: 20 }, () => String.fromCharCode(0x30A0 + Math.random() * 96)).join('\n')}
        </div>
      ))}
    </div>

    {/* Scan lines */}
    <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(16, 185, 129, 0.1) 2px, rgba(16, 185, 129, 0.1) 4px)'
    }} />

    {/* Top Bar */}
    <div className="relative z-10 flex items-center justify-between p-8 border-b border-emerald-500/30">
      <div className="flex items-center gap-4">
        <div className="p-4 border border-emerald-500/50 rounded-lg bg-emerald-500/10">
          <div className="text-emerald-400">{getTypeIcon()}</div>
        </div>
        <div>
          <h1 className="text-4xl font-mono text-emerald-400 font-bold tracking-wider">{getTypeTitle()}</h1>
          <p className="text-emerald-600 font-mono text-sm">// SYSTEM ACTIVE</p>
        </div>
      </div>
      
      <div className="text-right font-mono">
        <div className="text-5xl text-emerald-400 font-bold">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
        <div className="text-emerald-600 text-sm">
          [{currentTime.toLocaleDateString('en-IN')}]
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="aspect-video rounded-lg overflow-hidden border border-emerald-500/50 bg-black">
            {currentItem?.image ? (
              <img 
                src={getImageUrl(currentItem.image)} 
                alt={currentItem.title}
                className="w-full h-full object-cover opacity-80"
                style={{ filter: 'hue-rotate(90deg) saturate(1.5)' }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-emerald-400">{getTypeIcon()}</div>
              </div>
            )}
            
            <div className="absolute top-4 left-4 font-mono">
              <span className="px-3 py-1 bg-emerald-500/90 text-black text-xs font-bold uppercase">
                [{currentItem?.badge}]
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-900">
              <div 
                className="h-full bg-emerald-400 transition-all duration-1000"
                style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
              />
            </div>
          </div>

          <div className="mt-8 font-mono">
            <h2 className="text-4xl text-emerald-400 mb-4 line-clamp-2">
              {`> ${currentItem?.title}`}
            </h2>
            <p className="text-3xl text-emerald-500 mb-3">
              {currentItem?.subtitle}
            </p>
            <p className="text-emerald-700">
              // {currentItem?.detail1}
            </p>
          </div>
        </div>

        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="bg-emerald-950 p-6 rounded-lg border border-emerald-500/50">
              <QRCodeSVG 
                value={qrUrl}
                size={240}
                level="H"
                includeMargin={true}
                bgColor="#022c22"
                fgColor="#10b981"
              />
            </div>
            <p className="text-center mt-6 text-emerald-400 font-mono">
              [SCAN_TO_ACCESS]
            </p>
            <p className="text-emerald-700 font-mono mt-2">
              ITEM {currentIndex + 1}/{items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Sunset Template
const SunsetTemplate = ({ currentItem, items, currentIndex, qrUrl, showQR, rotationSpeed, countdown, getTypeIcon, getTypeTitle, currentTime }) => (
  <div className="h-screen flex flex-col bg-gradient-to-br from-orange-500 via-rose-500 to-purple-700 relative overflow-hidden">
    {/* Sun rays */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-yellow-400/30 to-transparent rounded-[100%] blur-3xl" />
    
    {/* Clouds */}
    <div className="absolute top-20 left-10 w-64 h-16 bg-white/10 rounded-full blur-2xl" />
    <div className="absolute top-32 right-20 w-48 h-12 bg-white/10 rounded-full blur-2xl" />

    {/* Top Bar */}
    <div className="relative z-10 flex items-center justify-between p-8">
      <div className="flex items-center gap-4">
        <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
          <div className="text-white">{getTypeIcon()}</div>
        </div>
        <div>
          <h1 className="text-4xl font-bold text-white drop-shadow-lg">{getTypeTitle()}</h1>
          <p className="text-white/60">ANANT Web Portal</p>
        </div>
      </div>
      
      <div className="text-right bg-white/10 backdrop-blur-sm rounded-2xl p-4">
        <div className="text-5xl font-bold text-white font-mono drop-shadow-lg">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-white/60 text-sm">
          {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </div>
      </div>
    </div>

    {/* Center Content */}
    <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="aspect-video rounded-3xl overflow-hidden bg-black/20 backdrop-blur-sm shadow-2xl">
            {currentItem?.image ? (
              <img 
                src={getImageUrl(currentItem.image)} 
                alt={currentItem.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400/30 to-purple-500/30">
                <div className="text-white">{getTypeIcon()}</div>
              </div>
            )}
            
            <div className="absolute top-4 left-4">
              <span className="px-4 py-2 bg-white/90 rounded-full text-sm font-bold text-purple-600">
                {currentItem?.badge}
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20">
              <div 
                className="h-full bg-white transition-all duration-1000"
                style={{ width: `${((rotationSpeed - countdown) / rotationSpeed) * 100}%` }}
              />
            </div>
          </div>

          <div className="mt-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-4 line-clamp-2 drop-shadow-lg">
              {currentItem?.title}
            </h2>
            <p className="text-4xl text-yellow-100 font-bold mb-3 drop-shadow">
              {currentItem?.subtitle}
            </p>
            <p className="text-white/70">
              {currentItem?.detail1}
            </p>
          </div>
        </div>

        {showQR && (
          <div className="flex flex-col items-center justify-center">
            <div className="bg-white p-6 rounded-3xl shadow-2xl">
              <QRCodeSVG 
                value={qrUrl}
                size={240}
                level="H"
                includeMargin={true}
                fgColor="#7c3aed"
              />
            </div>
            <p className="text-center mt-6 text-xl text-white font-bold drop-shadow">
              Scan to view
            </p>
            <p className="text-white/60 mt-2">
              {currentIndex + 1} of {items.length}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const DisplayTV = () => {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const boardRef = useRef(null);
  const [showQR, setShowQR] = useState(true);
  const [rotationSpeed, setRotationSpeed] = useState(8);
  const [showSettings, setShowSettings] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [countdown, setCountdown] = useState(rotationSpeed);
  const [selectedTemplate, setSelectedTemplate] = useState('hyper');

  const baseUrl = window.location.origin;
  const refCode = searchParams.get('ref') || '';

  // Fetch items
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      
      switch (type) {
        case 'auctions':
          response = await api.get('/auction/list?status=live');
          setItems(response.data?.map(a => ({
            id: a.id,
            title: a.title,
            image: a.image_url,
            subtitle: `Current Bid: ₹${a.current_bid?.toLocaleString() || a.starting_bid?.toLocaleString()}`,
            badge: 'LIVE',
            badgeColor: 'bg-red-500',
            detail1: `${a.bid_count || 0} bids`,
            detail2: a.end_time,
            link: `/auction/${a.id}`,
            type: 'auction'
          })) || []);
          break;
          
        case 'p2p':
          response = await api.get('/p2p/listings?status=active&limit=20');
          setItems(response.data?.listings?.map(p => ({
            id: p.id,
            title: p.title,
            image: p.images?.[0],
            subtitle: `₹${p.price?.toLocaleString()}`,
            badge: p.category,
            badgeColor: 'bg-blue-500',
            detail1: p.location || 'India',
            detail2: p.created_at,
            link: `/p2p/${p.id}`,
            type: 'p2p'
          })) || []);
          break;
          
        case 'products':
          response = await api.get('/products?limit=20');
          setItems(response.data?.products?.map(p => ({
            id: p.id,
            title: p.name,
            image: p.images?.[0] || p.image,
            subtitle: `₹${p.price?.toLocaleString()}`,
            badge: p.category || 'Product',
            badgeColor: 'bg-green-500',
            detail1: `${p.stock || 0} in stock`,
            detail2: null,
            link: `/products/${p.id}`,
            type: 'product'
          })) || []);
          break;
          
        default:
          const [auctionsRes, p2pRes, productsRes] = await Promise.all([
            api.get('/auction/list?status=live').catch(() => ({ data: [] })),
            api.get('/p2p/listings?status=active&limit=10').catch(() => ({ data: { listings: [] } })),
            api.get('/products?limit=10').catch(() => ({ data: { products: [] } }))
          ]);
          
          const combined = [
            ...(auctionsRes.data?.map(a => ({
              id: a.id,
              title: a.title,
              image: a.image_url,
              subtitle: `Current Bid: ₹${a.current_bid?.toLocaleString() || a.starting_bid?.toLocaleString()}`,
              badge: 'AUCTION',
              badgeColor: 'bg-red-500',
              detail1: `${a.bid_count || 0} bids`,
              link: `/auction/${a.id}`,
              type: 'auction'
            })) || []),
            ...(p2pRes.data?.listings?.map(p => ({
              id: p.id,
              title: p.title,
              image: p.images?.[0],
              subtitle: `₹${p.price?.toLocaleString()}`,
              badge: 'P2P',
              badgeColor: 'bg-blue-500',
              detail1: p.location || 'India',
              link: `/p2p/${p.id}`,
              type: 'p2p'
            })) || []),
            ...(productsRes.data?.products?.map(p => ({
              id: p.id,
              title: p.name,
              image: p.images?.[0] || p.image,
              subtitle: `₹${p.price?.toLocaleString()}`,
              badge: 'SHOP',
              badgeColor: 'bg-green-500',
              detail1: `${p.stock || 0} in stock`,
              link: `/products/${p.id}`,
              type: 'product'
            })) || [])
          ];
          setItems(combined);
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchItems();
    const refreshInterval = setInterval(fetchItems, 120000);
    return () => clearInterval(refreshInterval);
  }, [fetchItems]);

  // Auto-rotation
  useEffect(() => {
    if (!isPlaying || items.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length);
      setCountdown(rotationSpeed);
    }, rotationSpeed * 1000);
    return () => clearInterval(interval);
  }, [isPlaying, items.length, rotationSpeed]);

  // Countdown
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : rotationSpeed));
    }, 1000);
    return () => clearInterval(timer);
  }, [isPlaying, rotationSpeed]);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
          setCountdown(rotationSpeed);
          break;
        case 'ArrowRight':
          setCurrentIndex(prev => (prev + 1) % items.length);
          setCountdown(rotationSpeed);
          break;
        case ' ':
          e.preventDefault();
          setIsPlaying(prev => !prev);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'q':
        case 'Q':
          setShowQR(prev => !prev);
          break;
        case 's':
        case 'S':
          setShowSettings(prev => !prev);
          break;
        case 'Escape':
          if (showSettings) setShowSettings(false);
          else if (isFullscreen) toggleFullscreen();
          break;
        case '1': setSelectedTemplate('hyper'); break;
        case '2': setSelectedTemplate('minimal'); break;
        case '3': setSelectedTemplate('luxury'); break;
        case '4': setSelectedTemplate('ocean'); break;
        case '5': setSelectedTemplate('matrix'); break;
        case '6': setSelectedTemplate('sunset'); break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [items.length, rotationSpeed, isFullscreen, showSettings]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const currentItem = items[currentIndex];
  const qrUrl = currentItem ? `${baseUrl}${currentItem.link}${refCode ? `?ref=${refCode}` : ''}` : baseUrl;

  const getTypeIcon = () => {
    const iconClass = "w-8 h-8";
    switch (type) {
      case 'auctions': return <Gavel className={iconClass} />;
      case 'p2p': return <HomeIcon className={iconClass} />;
      case 'products': return <ShoppingBag className={iconClass} />;
      default: return <Tv className={iconClass} />;
    }
  };

  const getTypeTitle = () => {
    switch (type) {
      case 'auctions': return 'Live Auctions';
      case 'p2p': return 'P2P Marketplace';
      case 'products': return 'Products';
      default: return 'ANANT Display';
    }
  };

  // Template props
  const templateProps = {
    currentItem,
    items,
    currentIndex,
    qrUrl,
    showQR,
    rotationSpeed,
    countdown,
    getTypeIcon,
    getTypeTitle,
    currentTime
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-primary/20 rounded-full" />
            <div className="absolute inset-0 w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-white text-2xl mt-6">Loading Display...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          {getTypeIcon()}
          <h1 className="text-4xl font-bold text-white mt-4 mb-2">{getTypeTitle()}</h1>
          <p className="text-gray-400 text-xl">No items available</p>
          <button 
            onClick={() => navigate(-1)}
            className="mt-8 px-6 py-3 bg-primary rounded-full text-white font-bold"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render selected template
  const renderTemplate = () => {
    switch (selectedTemplate) {
      case 'minimal': return <MinimalTemplate {...templateProps} />;
      case 'luxury': return <LuxuryTemplate {...templateProps} />;
      case 'ocean': return <OceanTemplate {...templateProps} />;
      case 'matrix': return <MatrixTemplate {...templateProps} />;
      case 'sunset': return <SunsetTemplate {...templateProps} />;
      default: return <HyperTemplate {...templateProps} />;
    }
  };

  return (
    <div className="relative" data-testid="display-tv-page">
      <div ref={boardRef}>{renderTemplate()}</div>

      {/* Floating Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 p-3 bg-black/80 backdrop-blur-xl rounded-full border border-white/10">
        <button 
          onClick={() => {
            setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
            setCountdown(rotationSpeed);
          }}
          className="p-3 hover:bg-white/10 rounded-full transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        
        <button 
          onClick={() => setIsPlaying(!isPlaying)}
          className="p-3 bg-primary rounded-full hover:bg-primary/80 transition-colors"
        >
          {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white" />}
        </button>
        
        <button 
          onClick={() => {
            setCurrentIndex(prev => (prev + 1) % items.length);
            setCountdown(rotationSpeed);
          }}
          className="p-3 hover:bg-white/10 rounded-full transition-colors"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        <div className="w-px h-8 bg-white/20" />

        {/* Download buttons — JPG + PNG */}
        <button
          onClick={async () => {
            if (!boardRef.current) return;
            setDownloading(true);
            try {
              const canvas = await html2canvas(boardRef.current, { backgroundColor: '#000', scale: 2, useCORS: true, allowTaint: true, logging: false });
              const link = document.createElement('a');
              const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
              link.download = `display-${type || 'board'}-${stamp}.jpg`;
              link.href = canvas.toDataURL('image/jpeg', 0.92);
              document.body.appendChild(link); link.click(); document.body.removeChild(link);
            } catch (err) { alert('JPG export failed: ' + (err.message || 'unknown')); }
            finally { setDownloading(false); }
          }}
          disabled={downloading}
          className="p-3 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1 text-xs text-white"
          data-testid="display-download-jpg"
          title="Download as JPG"
        >
          <Download className="w-4 h-4 text-white" /> JPG
        </button>
        <button
          onClick={async () => {
            if (!boardRef.current) return;
            setDownloading(true);
            try {
              const canvas = await html2canvas(boardRef.current, { backgroundColor: null, scale: 2, useCORS: true, allowTaint: true, logging: false });
              const link = document.createElement('a');
              const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
              link.download = `display-${type || 'board'}-${stamp}.png`;
              link.href = canvas.toDataURL('image/png');
              document.body.appendChild(link); link.click(); document.body.removeChild(link);
            } catch (err) { alert('PNG export failed: ' + (err.message || 'unknown')); }
            finally { setDownloading(false); }
          }}
          disabled={downloading}
          className="p-3 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1 text-xs text-white"
          data-testid="display-download-png"
          title="Download as PNG (transparent bg)"
        >
          <Download className="w-4 h-4 text-white" /> PNG
        </button>

        <div className="w-px h-8 bg-white/20" />

        <button 
          onClick={() => setShowQR(!showQR)}
          className={`p-3 rounded-full transition-colors ${showQR ? 'bg-primary' : 'hover:bg-white/10'}`}
        >
          <QrCode className="w-5 h-5 text-white" />
        </button>
        
        <button 
          onClick={toggleFullscreen}
          className="p-3 hover:bg-white/10 rounded-full transition-colors"
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5 text-white" /> : <Maximize2 className="w-5 h-5 text-white" />}
        </button>
        
        <button 
          onClick={() => setShowSettings(true)}
          className="p-3 hover:bg-white/10 rounded-full transition-colors"
        >
          <Settings className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/20 rounded-xl">
                  <Palette className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-white">Display Settings</h3>
              </div>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
            
            {/* Template Selection */}
            <div className="mb-8">
              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-primary" />
                Choose Template
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.values(TEMPLATES).map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      selectedTemplate === template.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className={`w-full h-20 rounded-lg mb-3 ${template.preview}`} />
                    <p className="font-bold text-white text-sm">{template.name}</p>
                    <p className="text-xs text-gray-400">{template.description}</p>
                    {selectedTemplate === template.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Star className="w-3 h-3 text-white fill-current" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Other Settings */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-400 mb-3">Rotation Speed (seconds)</label>
                <input 
                  type="range" 
                  min="3" 
                  max="30" 
                  value={rotationSpeed}
                  onChange={(e) => setRotationSpeed(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>3s</span>
                  <span className="text-primary font-bold">{rotationSpeed}s</span>
                  <span>30s</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <span className="text-white">Show QR Code</span>
                <button 
                  onClick={() => setShowQR(!showQR)}
                  className={`w-14 h-7 rounded-full transition-colors flex items-center ${showQR ? 'bg-primary' : 'bg-gray-700'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showQR ? 'translate-x-8' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="p-4 bg-white/5 rounded-xl">
                <p className="text-sm text-gray-400 mb-3">Keyboard Shortcuts:</p>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <span className="text-gray-500 font-mono">← →</span><span className="text-white">Navigate</span>
                  <span className="text-gray-500 font-mono">Space</span><span className="text-white">Play/Pause</span>
                  <span className="text-gray-500 font-mono">F</span><span className="text-white">Fullscreen</span>
                  <span className="text-gray-500 font-mono">Q</span><span className="text-white">Toggle QR</span>
                  <span className="text-gray-500 font-mono">S</span><span className="text-white">Settings</span>
                  <span className="text-gray-500 font-mono">1-6</span><span className="text-white">Switch Templates</span>
                </div>
              </div>
            </div>
            
            <button 
              onClick={() => setShowSettings(false)}
              className="w-full mt-8 py-4 bg-primary rounded-full font-bold text-white hover:bg-primary/80 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Matrix animation style */}
      <style>{`
        @keyframes matrixFall {
          from { transform: translateY(-100%); }
          to { transform: translateY(100vh); }
        }
      `}</style>
    </div>
  );
};

export default DisplayTV;
